import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Mic,
  Square,
  RotateCcw,
  Sparkles,
  Download,
  ArrowRight,
  Loader2,
  Facebook,
  Twitter,
  Copy,
  ChevronLeft,
} from "lucide-react";
import "./StudioPage.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LS_SESSION = "aax_studio_session";

export default function StudioPage() {
  return (
    <div className="studio-root" data-testid="studio-root">
      <div className="studio-topbar">
        <Link to="/" className="studio-back" data-testid="studio-back-home">
          <ChevronLeft size={16} /> Back to site
        </Link>
        <div className="studio-brand">21 QUESTIONS · NJ GRIFFIN</div>
      </div>
      <StudioBooth />
    </div>
  );
}

function StudioBooth() {
  const [sessionId, setSessionId] = useState(null);
  const [queue, setQueue] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [pairs, setPairs] = useState([]);
  const [introUrl, setIntroUrl] = useState(null);
  const [introDone, setIntroDone] = useState(false);
  const [questionAudioUrl, setQuestionAudioUrl] = useState(null);
  const [questionAudioFile, setQuestionAudioFile] = useState(null);
  const [ttsBusy, setTtsBusy] = useState(false);
  const [ttsErr, setTtsErr] = useState("");

  const [recState, setRecState] = useState("idle");
  const [recBlob, setRecBlob] = useState(null);
  const [recBlobUrl, setRecBlobUrl] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [recErr, setRecErr] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef(null);

  const [followupBusy, setFollowupBusy] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [finalUrl, setFinalUrl] = useState(null);
  const [copyMsg, setCopyMsg] = useState("");

  const currentQuestion = queue[currentIdx];

  useEffect(() => {
    const savedSess = localStorage.getItem(LS_SESSION);
    if (savedSess) {
      (async () => {
        try {
          const r = await axios.post(`${API}/studio/load`, {
            session_id: savedSess,
          });
          if (r.data?.session_id) {
            setSessionId(r.data.session_id);
            setQueue(r.data.queue || []);
            setPairs(r.data.pairs || []);
            setCurrentIdx((r.data.pairs || []).length);
            if (r.data.intro_audio_url) {
              setIntroUrl(`${BACKEND_URL}${r.data.intro_audio_url}`);
            }
            // If they've already answered anything, skip the intro screen
            if ((r.data.pairs || []).length > 0) setIntroDone(true);
            return;
          }
        } catch {
          localStorage.removeItem(LS_SESSION);
        }
        startFresh();
      })();
    } else {
      startFresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startFresh = async () => {
    try {
      const r = await axios.post(`${API}/studio/start`, { count: 21 });
      setSessionId(r.data.session_id);
      setQueue(r.data.questions);
      setPairs([]);
      setCurrentIdx(0);
      setFinalUrl(null);
      setIntroDone(false);
      if (r.data.intro_audio_url) {
        setIntroUrl(`${BACKEND_URL}${r.data.intro_audio_url}`);
      } else {
        setIntroUrl(null);
      }
      localStorage.setItem(LS_SESSION, r.data.session_id);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!sessionId || !currentQuestion) {
      setQuestionAudioUrl(null);
      setQuestionAudioFile(null);
      return;
    }
    setQuestionAudioUrl(null);
    setTtsErr("");
    setTtsBusy(true);
    axios
      .post(`${API}/studio/tts`, {
        session_id: sessionId,
        question_id: currentQuestion.id,
        text: currentQuestion.q,
      })
      .then((r) => {
        setQuestionAudioUrl(`${BACKEND_URL}${r.data.audio_url}`);
        setQuestionAudioFile(r.data.filename);
      })
      .catch((e) => {
        setTtsErr(e.response?.data?.detail || "TTS failed");
      })
      .finally(() => setTtsBusy(false));

    setRecState("idle");
    setRecBlob(null);
    if (recBlobUrl) URL.revokeObjectURL(recBlobUrl);
    setRecBlobUrl(null);
    setTranscript("");
    setRecErr("");
    setElapsed(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, currentQuestion?.id]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (elapsedRef.current) clearInterval(elapsedRef.current);
      if (recBlobUrl) URL.revokeObjectURL(recBlobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = async () => {
    setRecErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });
      streamRef.current = stream;
      let mimeType = "audio/webm;codecs=opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "audio/webm";
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "";
        }
      }
      const rec = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: rec.mimeType || "audio/webm",
        });
        const url = URL.createObjectURL(blob);
        setRecBlob(blob);
        setRecBlobUrl(url);
        setRecState("reviewing");
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        if (elapsedRef.current) {
          clearInterval(elapsedRef.current);
          elapsedRef.current = null;
        }
      };
      rec.start(500);
      setRecState("recording");
      setElapsed(0);
      elapsedRef.current = setInterval(
        () => setElapsed((e) => e + 1),
        1000,
      );
    } catch (e) {
      setRecErr(
        "Microphone access denied. Please allow mic access and try again.",
      );
      setRecState("idle");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
  };

  const retake = () => {
    if (recBlobUrl) URL.revokeObjectURL(recBlobUrl);
    setRecBlob(null);
    setRecBlobUrl(null);
    setTranscript("");
    setRecState("idle");
    setElapsed(0);
  };

  const useThisTake = async () => {
    if (!recBlob || !sessionId || !currentQuestion) return;
    setRecState("uploading");
    setRecErr("");
    try {
      const fd = new FormData();
      fd.append("session_id", sessionId);
      fd.append("question_id", currentQuestion.id);
      fd.append("question_text", currentQuestion.q);
      fd.append("question_audio_file", questionAudioFile || "");
      fd.append("audio", recBlob, "answer.webm");
      const r = await axios.post(`${API}/studio/transcribe`, fd);
      setTranscript(r.data.transcript || "");
      const newPair = {
        question_id: currentQuestion.id,
        question_text: currentQuestion.q,
        transcript: r.data.transcript || "",
        answer_audio_url: `${BACKEND_URL}${r.data.answer_audio_url}`,
      };
      setPairs((p) => {
        const filtered = p.filter((x) => x.question_id !== currentQuestion.id);
        return [...filtered, newPair];
      });
      setRecState("done");
    } catch (e) {
      setRecErr(e.response?.data?.detail || "Upload/transcribe failed");
      setRecState("reviewing");
    }
  };

  const nextCurated = () => {
    setCurrentIdx((i) => Math.min(i + 1, queue.length));
  };

  const askFollowup = async () => {
    setFollowupBusy(true);
    try {
      const r = await axios.post(`${API}/studio/followup`, {
        session_id: sessionId,
      });
      const q = r.data.question;
      const newQueue = [...queue];
      newQueue.splice(currentIdx + 1, 0, q);
      setQueue(newQueue);
      setCurrentIdx(currentIdx + 1);
    } catch (e) {
      setRecErr(e.response?.data?.detail || "Follow-up failed");
    } finally {
      setFollowupBusy(false);
    }
  };

  const finalize = async () => {
    setFinalizing(true);
    try {
      const r = await axios.post(`${API}/studio/finalize`, {
        session_id: sessionId,
      });
      setFinalUrl(`${BACKEND_URL}${r.data.download_url}`);
    } catch (e) {
      setRecErr(e.response?.data?.detail || "Finalize failed");
    } finally {
      setFinalizing(false);
    }
  };

  const startOver = async () => {
    localStorage.removeItem(LS_SESSION);
    setIntroDone(false);
    await startFresh();
  };

  const shareUrl = "https://africanamericanxperience.com/";
  const shareText =
    "21 Questions with NJ Griffin — the conversation behind Stones the Builder Refused, Volume I. Listen:";

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopyMsg("Copied!");
      setTimeout(() => setCopyMsg(""), 2500);
    } catch {
      setCopyMsg("Copy failed");
    }
  };

  const doneCount = pairs.length;
  const isFinished = currentIdx >= queue.length;

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  return (
    <div className="studio-booth">
      <div className="studio-progress">
        <div className="prog-count" data-testid="studio-progress">
          {doneCount} of {queue.length} answered
        </div>
        <div className="prog-bar">
          <div
            className="prog-fill"
            style={{
              width: `${queue.length ? (doneCount / queue.length) * 100 : 0}%`,
            }}
          />
        </div>
      </div>

      {sessionId && queue.length > 0 && !introDone && !finalUrl && pairs.length === 0 ? (
        <div className="studio-intro-card" data-testid="studio-intro-card">
          <div className="intro-badge">
            <Sparkles size={14} /> ON AIR
          </div>
          <h2>Your host is ready.</h2>
          <p className="intro-sub">
            <strong>▶️ Press play to hear your opening.</strong> When it finishes, we'll jump straight into question 1.
          </p>
          {introUrl ? (
            <audio
              controls
              autoPlay
              src={introUrl}
              className="intro-audio"
              data-testid="studio-intro-audio"
              onEnded={() => setIntroDone(true)}
            />
          ) : (
            <div className="q-audio-loading">
              <Loader2 size={14} className="spin" /> Warming up the mic…
            </div>
          )}
          <div className="intro-actions">
            <button
              className="btn-primary"
              onClick={() => setIntroDone(true)}
              data-testid="studio-start-interview"
            >
              Start the interview <ArrowRight size={16} />
            </button>
          </div>
          <div className="intro-hint">
            (or wait for the intro to finish — we'll advance automatically)
          </div>
        </div>
      ) : finalUrl ? (
        <div className="studio-final" data-testid="studio-final">
          <div className="final-badge">
            <Sparkles size={14} /> WRAPPED
          </div>
          <h2>Your interview is ready.</h2>
          <p className="final-sub">
            <strong>▶️ Tap play to hear the full segment.</strong> Intro, all {doneCount} Q&amp;As,
            and outro stitched together. Not right? Scroll down for retake options.
          </p>
          <audio
            controls
            src={finalUrl}
            className="final-audio"
            data-testid="final-audio"
          />
          <div className="final-actions">
            <a
              className="btn-primary"
              href={finalUrl}
              download="21questions_njgriffin.mp3"
              data-testid="final-download"
            >
              <Download size={16} /> Download MP3
            </a>
            <button
              className="btn-ghost"
              onClick={() => {
                setFinalUrl(null);
                setCurrentIdx(0);
              }}
              data-testid="final-retake-any"
            >
              <RotateCcw size={16} /> Retake a question
            </button>
            <a
              className="btn-ghost"
              target="_blank"
              rel="noreferrer"
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                shareUrl,
              )}`}
              data-testid="final-share-facebook"
            >
              <Facebook size={16} /> Share
            </a>
            <button
              className="btn-ghost"
              onClick={copyShare}
              data-testid="final-copy-link"
            >
              <Copy size={16} /> {copyMsg || "Copy caption"}
            </button>
          </div>
          <div className="final-transcript" data-testid="final-transcript">
            <h3>Transcript</h3>
            {pairs.map((p, i) => (
              <div className="pair" key={p.question_id + i}>
                <div className="pair-q">Q: {p.question_text}</div>
                <div className="pair-a">{p.transcript || "(no transcript)"}</div>
              </div>
            ))}
          </div>
          <button
            className="btn-link"
            onClick={startOver}
            data-testid="studio-start-over"
          >
            Start a new interview
          </button>
        </div>
      ) : isFinished && queue.length > 0 ? (
        <div className="studio-finish-cta" data-testid="studio-finish-cta">
          <h2>You&apos;ve answered all {queue.length} questions.</h2>
          <p>
            Ready to bundle everything into one shareable MP3? It&apos;ll add a
            warm intro + outro and stitch the questions with your answers.
          </p>
          <p className="finish-hint">
            💡 <strong>Preview first</strong> — hit finalize to hear the whole
            segment. If anything needs a retake, you can re-record any question
            and finalize again. Nothing is locked until you download.
          </p>
          <div className="finish-actions">
            <button
              className="btn-primary"
              onClick={finalize}
              disabled={finalizing || pairs.length === 0}
              data-testid="studio-finalize-btn"
            >
              {finalizing ? (
                <>
                  <Loader2 size={16} className="spin" /> Mixing your segment…
                </>
              ) : (
                <>
                  Preview + finalize <ArrowRight size={16} />
                </>
              )}
            </button>
            <button
              className="btn-ghost"
              onClick={askFollowup}
              disabled={followupBusy || pairs.length === 0}
              data-testid="studio-followup-more"
            >
              {followupBusy ? (
                <Loader2 size={16} className="spin" />
              ) : (
                <>
                  <Sparkles size={14} /> One more (AI follow-up)
                </>
              )}
            </button>
          </div>
        </div>
      ) : currentQuestion ? (
        <div className="studio-card" data-testid="studio-card">
          <div className="q-cat">{(currentQuestion.cat || "").toUpperCase()}</div>
          <div className="q-text" data-testid="studio-question-text">
            {currentQuestion.q}
          </div>

          <div className="q-audio-row">
            {ttsBusy ? (
              <div className="q-audio-loading">
                <Loader2 size={14} className="spin" /> Loading interviewer
                voice…
              </div>
            ) : questionAudioUrl ? (
              <audio
                controls
                src={questionAudioUrl}
                data-testid="studio-question-audio"
              />
            ) : ttsErr ? (
              <div className="q-audio-err">{ttsErr}</div>
            ) : null}
          </div>

          <div className="rec-panel">
            {recState === "idle" && (
              <button
                className="btn-rec"
                onClick={startRecording}
                data-testid="studio-record-start"
              >
                <Mic size={22} /> Tap to answer
              </button>
            )}
            {recState === "recording" && (
              <div className="rec-live">
                <div className="rec-pulse" />
                <div className="rec-timer">{fmtTime(elapsed)}</div>
                <button
                  className="btn-stop"
                  onClick={stopRecording}
                  data-testid="studio-record-stop"
                >
                  <Square size={18} /> Stop
                </button>
              </div>
            )}
            {recState === "reviewing" && recBlobUrl && (
              <div className="rec-review">
                <audio
                  controls
                  src={recBlobUrl}
                  data-testid="studio-answer-preview"
                />
                <div className="review-actions">
                  <button
                    className="btn-ghost"
                    onClick={retake}
                    data-testid="studio-retake"
                  >
                    <RotateCcw size={16} /> Retake
                  </button>
                  <button
                    className="btn-primary"
                    onClick={useThisTake}
                    data-testid="studio-use-take"
                  >
                    Use this take <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
            {recState === "uploading" && (
              <div className="rec-uploading">
                <Loader2 size={20} className="spin" /> Transcribing your
                answer…
              </div>
            )}
            {recState === "done" && (
              <div className="rec-done" data-testid="studio-answer-done">
                <div className="done-check">✓ Locked in</div>
                {transcript && (
                  <div className="done-transcript">&ldquo;{transcript}&rdquo;</div>
                )}
                <div className="done-actions">
                  <button
                    className="btn-ghost"
                    onClick={retake}
                    data-testid="studio-retake-after"
                  >
                    <RotateCcw size={16} /> Redo this one
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={askFollowup}
                    disabled={followupBusy}
                    data-testid="studio-followup-btn"
                  >
                    {followupBusy ? (
                      <Loader2 size={16} className="spin" />
                    ) : (
                      <>
                        <Sparkles size={14} /> AI follow-up
                      </>
                    )}
                  </button>
                  <button
                    className="btn-primary"
                    onClick={nextCurated}
                    data-testid="studio-next-question"
                  >
                    Next question <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
            {recErr && <div className="rec-err">{recErr}</div>}
          </div>
        </div>
      ) : (
        <div className="studio-loading" data-testid="studio-loading">
          <div className="intro-badge">
            <Sparkles size={14} /> WARMING UP
          </div>
          <Loader2 size={22} className="spin" />
          <div className="studio-loading-text">
            <strong>Setting up the studio…</strong>
            <span>Your host is stepping into the booth. This takes ~20 seconds on the first visit.</span>
          </div>
        </div>
      )}
    </div>
  );
}
